/* eslint-disable react/no-array-index-key */
// The parsed JD is static for a given payload and never reorders, so positional
// keys are stable here.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import {
  parseJD,
  buildNamedSections,
  jdMeta,
  isNA,
  splitTop,
  sentenceCase,
  renderInline,
} from './seniorFlowJd';

function renderList(list, prefix) {
  const Tag = list.ordered ? 'ol' : 'ul';
  return (
    <Tag className="slf-owns">
      {list.items.map((it, i) => (
        <li key={`${prefix}-${i}`}>
          {renderInline(it.text)}
          {it.children ? renderList(it.children, `${prefix}-${i}`) : null}
        </li>
      ))}
    </Tag>
  );
}

function renderValue(item, prefix) {
  if (item.list) {
    return (
      <>
        {item.value ? renderInline(item.value) : null}
        {renderList(item.list, prefix)}
      </>
    );
  }
  if (isNA(item.value)) {
    return <span className="slf-jd-na">Not in the brief yet</span>;
  }
  if (/skill|domain/i.test(item.key)) {
    return (
      <ul className="slf-tag-chips">
        {splitTop(item.value).map((p, i) => {
          const mm = p.match(/^(.*?)\s*\(([^)]*)\)\s*$/);
          return (
            <li className="slf-tag-chip" key={`${prefix}-${i}`}>
              {mm ? (
                <>
                  {renderInline(mm[1])}{' '}
                  <span className="slf-q">{renderInline(mm[2])}</span>
                </>
              ) : (
                renderInline(p)
              )}
            </li>
          );
        })}
      </ul>
    );
  }
  return renderInline(item.value);
}

function renderBlock(block, prefix) {
  if (block.type === 'p') {
    return (
      <p className="slf-read" key={prefix}>
        {renderInline(block.text)}
      </p>
    );
  }
  if (block.type === 'sub') {
    return (
      <h4 className="slf-jd-sub" key={prefix}>
        {renderInline(block.text)}
      </h4>
    );
  }
  if (block.type === 'list') {
    return <div key={prefix}>{renderList(block, prefix)}</div>;
  }
  if (block.type === 'kv') {
    return (
      <dl className="slf-jd-kv" key={prefix}>
        {block.items.map((it, i) => (
          <div className="slf-jd-row" key={`${prefix}-${i}`}>
            <dt>{sentenceCase(it.key)}</dt>
            <dd>{renderValue(it, `${prefix}-${i}`)}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return null;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function JobDescription({ md }) {
  const sections = useMemo(() => parseJD(md), [md]);
  const named = useMemo(() => buildNamedSections(sections), [sections]);
  const meta = useMemo(() => jdMeta(md, sections), [md, sections]);

  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const navRef = useRef(null);
  const sectionRefs = useRef({});
  const rootRef = useRef(null);

  const preview = useMemo(() => {
    let text = '';
    sections.some((s) => {
      const p = s.blocks.find((b) => b.type === 'p');
      if (p) text = p.text;
      return !!p;
    });
    return text;
  }, [sections]);

  // Scroll-spy: highlight the section under the sticky sub-nav.
  useEffect(() => {
    if (!open || named.length < 3) return undefined;
    let ticking = false;
    const compute = () => {
      ticking = false;
      const nav = navRef.current;
      // The line a section must pass to count as active: just below the stuck
      // sub-nav (whose own top already sits under the app header).
      const line = nav ? nav.getBoundingClientRect().bottom + 8 : 80;
      let active = named[0].id;
      named.forEach((n) => {
        const el = sectionRefs.current[n.id];
        if (el && el.getBoundingClientRect().top <= line) active = n.id;
      });
      // A short final section can't scroll under the line; at page bottom, treat
      // the last section as active so its tab lights up.
      const atBottom =
        Math.ceil(window.innerHeight + window.scrollY) >=
        document.documentElement.scrollHeight - 2;
      if (atBottom) active = named[named.length - 1].id;
      setActiveId(active);
      if (nav) {
        const a = nav.querySelector(`a[data-id="${active}"]`);
        if (a) {
          const l = a.offsetLeft - nav.offsetLeft;
          const r = l + a.offsetWidth;
          if (l < nav.scrollLeft + 16) nav.scrollLeft = l - 16;
          else if (r > nav.scrollLeft + nav.clientWidth - 16) {
            nav.scrollLeft = r - nav.clientWidth + 16;
          }
        }
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(compute);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    compute();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, named]);

  if (!sections.length) return null;

  const jumpTo = (id) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    el.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
    const h = el.querySelector('h3');
    if (h) h.focus({ preventScroll: true });
  };

  const closeAndReturn = () => {
    setOpen(false);
    if (rootRef.current) {
      rootRef.current.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <section
      className="slf-jd mt-2"
      ref={rootRef}
      data-open={open ? 'true' : 'false'}
      aria-labelledby="slf-jdHead"
    >
      <h2 id="slf-jdHead">Job description</h2>

      {!open && preview ? (
        <p className="slf-read slf-jd-preview">{renderInline(preview)}</p>
      ) : null}

      <div className="slf-jd-bar">
        <button
          className="slf-btn slf-btn-secondary slf-jd-toggle"
          type="button"
          aria-expanded={open}
          aria-controls="slf-jdPanel"
          onClick={() => setOpen((o) => !o)}
        >
          <span className="slf-lbl">
            {open ? 'Hide job description' : 'Read the full job description'}
          </span>
          <ChevronDown className="slf-caret" aria-hidden="true" />
        </button>
        {!open ? (
          <span className="slf-note">
            {meta.titled} sections, about {meta.mins}{' '}
            {meta.mins === 1 ? 'minute' : 'minutes'} to read
          </span>
        ) : null}
      </div>

      <div id="slf-jdPanel" hidden={!open}>
        {named.length >= 3 ? (
          <nav
            className="slf-jd-nav"
            ref={navRef}
            aria-label="Job description sections"
          >
            {named.map((n) => (
              <a
                key={n.id}
                href={`#${n.id}`}
                data-id={n.id}
                aria-current={activeId === n.id ? 'true' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  jumpTo(n.id);
                }}
              >
                {n.short}
              </a>
            ))}
          </nav>
        ) : null}

        <div>
          {named.map((n) => (
            <section
              key={n.id}
              id={n.id}
              className="slf-jd-sec"
              ref={(el) => {
                sectionRefs.current[n.id] = el;
              }}
              aria-label={n.full ? undefined : 'Overview'}
              aria-labelledby={n.full ? `${n.id}-h` : undefined}
            >
              {n.full ? (
                <h3 id={`${n.id}-h`} tabIndex={-1}>
                  {n.full}
                </h3>
              ) : null}
              {n.section.blocks.map((b, bi) => renderBlock(b, `${n.id}-${bi}`))}
            </section>
          ))}
        </div>

        <div className="slf-jd-end">
          <button
            className="slf-btn slf-btn-secondary slf-jd-toggle"
            type="button"
            aria-expanded
            aria-controls="slf-jdPanel"
            onClick={closeAndReturn}
          >
            <span className="slf-lbl">Hide job description</span>
            <ChevronDown className="slf-caret" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}

JobDescription.propTypes = {
  md: PropTypes.string,
};

JobDescription.defaultProps = {
  md: '',
};

export default JobDescription;
